import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  ArrowLeft, 
  Save, 
  MessageCircle, 
  Mail,
  Calendar,
  MapPin,
  User,
  Phone,
  FileText,
  Palette,
  Users,
  PartyPopper
} from "lucide-react";

const ClienteDetalle = () => {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [pago, setPago] = useState(null);
  const [invitacion, setInvitacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clienteRes, pagosRes, invitacionesRes] = await Promise.all([
          axios.get(`${API}/clientes/${clienteId}`),
          axios.get(`${API}/pagos`),
          axios.get(`${API}/invitaciones`)
        ]);
        
        setCliente(clienteRes.data);
        setPago(pagosRes.data.find(p => p.cliente_id === clienteId));
        setInvitacion(invitacionesRes.data.find(i => i.cliente_id === clienteId));
      } catch {
        toast.error("Error al cargar los datos del cliente");
        navigate("/panel/clientes");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clienteId, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clientes/${clienteId}`, cliente);
      toast.success("Cliente actualizado");
    } catch (error) {
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCliente(prev => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/clientes/${clienteId}`, { estatus: newStatus });
      setCliente(prev => ({ ...prev, estatus: newStatus }));
      toast.success("Estado actualizado");
    } catch (error) {
      toast.error("Error al actualizar el estado");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendiente_pago: { label: "Pendiente Pago", className: "badge-pending" },
      comprobante_enviado: { label: "Comprobante Enviado", className: "badge-pending" },
      validado: { label: "Validado", className: "badge-validated" },
      en_diseno: { label: "En Diseño", className: "badge-design" },
      entregado: { label: "Entregado", className: "badge-delivered" },
      seguimiento_evento: { label: "Seguimiento", className: "badge-event" },
    };
    return badges[status] || { label: status, className: "badge" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!cliente) return null;

  const badge = getStatusBadge(cliente.estatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/panel/clientes")}
            className="border-stone-300"
            data-testid="btn-volver"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="font-display text-2xl text-stone-900">{cliente.nombre}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={`badge ${badge.className}`}>{badge.label}</span>
              <span className="text-stone-500 text-sm capitalize">{cliente.tipo_servicio?.replace(/_/g, ' ')}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate(`/panel/cotizaciones?nuevo=${clienteId}`)}
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="btn-crear-cotizacion-cliente">
            <FileText className="w-4 h-4 mr-2" /> Crear Cotizacion
          </Button>
          <a href={`https://wa.me/52${cliente.whatsapp}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-stone-300" data-testid="btn-whatsapp-cliente">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
          </a>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="btn-guardar-cliente">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      {/* Status Change */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-stone-600">Cambiar estado:</Label>
            <Select value={cliente.estatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-48 border-stone-300" data-testid="select-estatus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente_pago">Pendiente Pago</SelectItem>
                <SelectItem value="comprobante_enviado">Comprobante Enviado</SelectItem>
                <SelectItem value="validado">Validado</SelectItem>
                <SelectItem value="en_diseno">En Diseño</SelectItem>
                <SelectItem value="entregado">Entregado</SelectItem>
                <SelectItem value="seguimiento_evento">Seguimiento Evento</SelectItem>
                <SelectItem value="confirmado">Confirmado</SelectItem>
                <SelectItem value="realizado">Realizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="datos" className="space-y-6">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="datos" data-testid="tab-datos">
            <User className="w-4 h-4 mr-2" />
            Datos
          </TabsTrigger>
          <TabsTrigger value="evento" data-testid="tab-evento">
            <Calendar className="w-4 h-4 mr-2" />
            Evento
          </TabsTrigger>
          <TabsTrigger value="invitacion" data-testid="tab-invitacion">
            <Mail className="w-4 h-4 mr-2" />
            Invitación
          </TabsTrigger>
          <TabsTrigger value="planeacion" data-testid="tab-planeacion">
            <PartyPopper className="w-4 h-4 mr-2" />
            Planeación
          </TabsTrigger>
        </TabsList>

        {/* Datos Tab */}
        <TabsContent value="datos">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-900" />
                Datos del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre completo</Label>
                  <Input 
                    id="nombre"
                    value={cliente.nombre || ""}
                    onChange={(e) => handleInputChange("nombre", e.target.value)}
                    className="border-stone-300"
                    data-testid="input-edit-nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="correo">Correo electrónico</Label>
                  <Input 
                    id="correo"
                    value={cliente.correo || ""}
                    onChange={(e) => handleInputChange("correo", e.target.value)}
                    className="border-stone-300"
                    data-testid="input-edit-correo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input 
                    id="telefono"
                    value={cliente.telefono || ""}
                    onChange={(e) => handleInputChange("telefono", e.target.value)}
                    className="border-stone-300"
                    data-testid="input-edit-telefono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input 
                    id="whatsapp"
                    value={cliente.whatsapp || ""}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    className="border-stone-300"
                    data-testid="input-edit-whatsapp"
                  />
                </div>
              </div>

              {/* Pago Info */}
              {pago && (
                <div className="pt-6 border-t border-stone-200">
                  <h3 className="font-display text-lg text-stone-900 mb-4">Información de Pago</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Estado</p>
                      <p className="font-medium text-stone-900 capitalize mt-1">{pago.estatus}</p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Monto</p>
                      <p className="font-medium text-stone-900 mt-1">${pago.monto} MXN</p>
                    </div>
                    <div className="p-4 bg-stone-50 rounded-sm">
                      <p className="text-xs text-stone-500 uppercase tracking-wider">Método</p>
                      <p className="font-medium text-stone-900 capitalize mt-1">{pago.metodo}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evento Tab */}
        <TabsContent value="evento">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-900" />
                Datos del Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Tipo de evento</Label>
                  <Input 
                    value={cliente.tipo_evento || ""}
                    onChange={(e) => handleInputChange("tipo_evento", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del festejado</Label>
                  <Input 
                    value={cliente.nombre_festejado || ""}
                    onChange={(e) => handleInputChange("nombre_festejado", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha del evento</Label>
                  <Input 
                    type="date"
                    value={cliente.fecha_evento || ""}
                    onChange={(e) => handleInputChange("fecha_evento", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hora inicio</Label>
                    <Input 
                      type="time"
                      value={cliente.hora_inicio || ""}
                      onChange={(e) => handleInputChange("hora_inicio", e.target.value)}
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora fin</Label>
                    <Input 
                      type="time"
                      value={cliente.hora_fin || ""}
                      onChange={(e) => handleInputChange("hora_fin", e.target.value)}
                      className="border-stone-300"
                    />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Ubicación
                  </Label>
                  <Input 
                    value={cliente.ubicacion || ""}
                    onChange={(e) => handleInputChange("ubicacion", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Enlace Google Maps</Label>
                  <Input 
                    value={cliente.google_maps || ""}
                    onChange={(e) => handleInputChange("google_maps", e.target.value)}
                    className="border-stone-300"
                  />
                  {cliente.google_maps && (
                    <a 
                      href={cliente.google_maps}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-900 hover:underline"
                    >
                      Ver en Google Maps
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitación Tab */}
        <TabsContent value="invitacion">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Palette className="w-5 h-5 text-emerald-900" />
                Diseño de Invitación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Tema o estilo</Label>
                  <Input 
                    value={cliente.tema || ""}
                    onChange={(e) => handleInputChange("tema", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Colorimetría</Label>
                  <Input 
                    value={cliente.colorimetria || ""}
                    onChange={(e) => handleInputChange("colorimetria", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código de vestimenta</Label>
                  <Input 
                    value={cliente.codigo_vestimenta || ""}
                    onChange={(e) => handleInputChange("codigo_vestimenta", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp para RSVP</Label>
                  <Input 
                    value={cliente.rsvp_whatsapp || ""}
                    onChange={(e) => handleInputChange("rsvp_whatsapp", e.target.value)}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Mensaje de invitación</Label>
                  <Textarea 
                    value={cliente.mensaje_invitacion || ""}
                    onChange={(e) => handleInputChange("mensaje_invitacion", e.target.value)}
                    rows={4}
                    className="border-stone-300"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Referencias o inspiración</Label>
                  <Textarea 
                    value={cliente.referencias || ""}
                    onChange={(e) => handleInputChange("referencias", e.target.value)}
                    rows={3}
                    className="border-stone-300"
                  />
                </div>
              </div>

              {/* Invitación Status */}
              {invitacion && (
                <div className="pt-6 border-t border-stone-200">
                  <h3 className="font-display text-lg text-stone-900 mb-4">Estado de la Invitación</h3>
                  <div className="p-4 bg-stone-50 rounded-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-stone-600">Estado actual:</span>
                      <span className={`badge ${invitacion.estatus === 'entregada' ? 'badge-validated' : 'badge-design'}`}>
                        {invitacion.estatus?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  <Link to="/panel/invitaciones" className="text-sm text-emerald-900 hover:underline mt-2 inline-block">
                    Ir al módulo de invitaciones
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planeación Tab */}
        <TabsContent value="planeacion">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-900" />
                  Planeación del Evento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Total de invitados</Label>
                    <Input 
                      type="number"
                      value={cliente.total_invitados || ""}
                      onChange={(e) => handleInputChange("total_invitados", parseInt(e.target.value) || null)}
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adultos</Label>
                    <Input 
                      type="number"
                      value={cliente.adultos || ""}
                      onChange={(e) => handleInputChange("adultos", parseInt(e.target.value) || null)}
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Niños</Label>
                    <Input 
                      type="number"
                      value={cliente.ninos || ""}
                      onChange={(e) => handleInputChange("ninos", parseInt(e.target.value) || null)}
                      className="border-stone-300"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Servicios requeridos</Label>
                  <div className="p-4 bg-stone-50 rounded-sm">
                    {cliente.servicios_requeridos?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {cliente.servicios_requeridos.map((servicio) => (
                          <span key={servicio} className="badge bg-emerald-100 text-emerald-800">
                            {servicio}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-stone-500 text-sm">Sin servicios especificados</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Alergias alimentarias</Label>
                    <Input 
                      value={cliente.alergias || ""}
                      onChange={(e) => handleInputChange("alergias", e.target.value)}
                      className="border-stone-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Necesidades especiales</Label>
                    <Input 
                      value={cliente.necesidades_especiales || ""}
                      onChange={(e) => handleInputChange("necesidades_especiales", e.target.value)}
                      className="border-stone-300"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notas adicionales</Label>
                  <Textarea 
                    value={cliente.notas || ""}
                    onChange={(e) => handleInputChange("notas", e.target.value)}
                    rows={4}
                    className="border-stone-300"
                  />
                </div>

                {/* Create Event Button */}
                <div className="pt-4 border-t border-stone-200">
                  <Link to="/panel/eventos">
                    <Button className="btn-accent" data-testid="btn-crear-evento">
                      <PartyPopper className="w-4 h-4 mr-2" />
                      Crear expediente de evento
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClienteDetalle;
